import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../../../lib/api'
import type { OnboardingStepKey } from '../../../config/onboardingRegistry'
import { CREDIT_BALANCE_KEY } from '../../credits/useCreditBalance'
import { useOnboardingGuidedStore } from '../../../store/onboardingGuidedStore'
import { syncOnboardingForfeitCache } from '../../../lib/onboarding/onboardingForfeitCache'

import type { OnboardingForfeitReason } from '../../../lib/analytics/onboardingAnalytics'

export type { OnboardingForfeitReason, OnboardingStepKey }

export type OnboardingStepRole = 'core' | 'optional' | 'challenge'

export interface OnboardingStepStatus {
  key: OnboardingStepKey
  completed: boolean
  points: number
  reward_granted: boolean
  role?: OnboardingStepRole
}

export interface OnboardingStatus {
  steps: OnboardingStepStatus[]
  completed_count: number
  total_count: number
  remaining_count: number
  all_completed: boolean
  completion_bonus: number
  points_at_stake: number
  reward_points: number
  reward_granted: boolean
  reward_granted_at: string | null
  dismissed: boolean
  visible: boolean
  forfeited: boolean
  auto_eligible: boolean
  can_opt_in: boolean
  opted_in: boolean
}

export const ONBOARDING_STATUS_QUERY_KEY = ['onboarding', 'status'] as const

function cacheStatus(status: OnboardingStatus): OnboardingStatus {
  syncOnboardingForfeitCache(status)
  return status
}

export function useOnboardingStatus() {
  return useQuery({
    queryKey: ONBOARDING_STATUS_QUERY_KEY,
    queryFn: async (): Promise<OnboardingStatus> => {
      const { data } = await api.get('/onboarding/status')
      return cacheStatus(data)
    },
    staleTime: 10_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
}

export function useRecordOnboardingMilestone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (stepKey: OnboardingStepKey) => {
      const { data } = await api.post('/onboarding/milestones', { step_key: stepKey })
      return data as { recorded: boolean; status: OnboardingStatus }
    },
    onSuccess: (data) => {
      qc.setQueryData(ONBOARDING_STATUS_QUERY_KEY, cacheStatus(data.status))
      qc.invalidateQueries({ queryKey: CREDIT_BALANCE_KEY })
    },
  })
}

export function useDismissOnboarding() {
  const qc = useQueryClient()
  const stopGuided = useOnboardingGuidedStore((s) => s.stop)
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/onboarding/dismiss')
      return data as OnboardingStatus
    },
    onSuccess: (data) => {
      qc.setQueryData(ONBOARDING_STATUS_QUERY_KEY, cacheStatus(data))
      stopGuided()
    },
  })
}

export function useForfeitOnboarding() {
  const qc = useQueryClient()
  const stopGuided = useOnboardingGuidedStore((s) => s.stop)
  return useMutation({
    mutationFn: async (reason?: OnboardingForfeitReason) => {
      const { data } = await api.post('/onboarding/forfeit', {
        reason: reason ?? undefined,
      })
      return data as OnboardingStatus
    },
    onSuccess: (data) => {
      qc.setQueryData(ONBOARDING_STATUS_QUERY_KEY, cacheStatus(data))
      stopGuided()
    },
  })
}

export function useRestoreOnboarding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      try {
        const { data } = await api.post('/onboarding/dismiss', { restore: true })
        return data as OnboardingStatus
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status !== 404) throw err
        const { data } = await api.post('/onboarding/restore')
        return data as OnboardingStatus
      }
    },
    onSuccess: (data) => {
      qc.setQueryData(ONBOARDING_STATUS_QUERY_KEY, cacheStatus(data))
      void qc.invalidateQueries({ queryKey: ONBOARDING_STATUS_QUERY_KEY })
    },
  })
}

/** Persist guide engagement so mid-guide product usage does not hide the checklist. */
export function useStartOnboardingGuide() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/onboarding/start')
      return data as OnboardingStatus
    },
    onSuccess: (data) => {
      qc.setQueryData(ONBOARDING_STATUS_QUERY_KEY, cacheStatus(data))
    },
  })
}
